import { Menu, MenuItem, MenuList, useDisclosure } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type UpdateClientMutation,
  useDeleteClientMutation,
  useUpdateClientMutation,
} from "@src/__generated__/graphql";
import { ModalConfirm } from "@src/components/ui-kit";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { type FunctionComponent, type ReactNode } from "react";
import { ModalClient } from "./modals/client/ModalClient";
import type { ClientModel } from "./models";

type Props = {
  trigger: ReactNode;
  client: Pick<ClientModel, "id" | "name" | "image" | "workspaceCompany">;
  onAfterUpdate?: (client: UpdateClientMutation["updateClient"]) => void;
  onAfterDelete?: () => void;
};

export const PopoverClientActions: FunctionComponent<
  React.PropsWithChildren<Props>
> = observer(function PopoverClientActions({
  trigger,
  client,
  onAfterDelete,
  onAfterUpdate,
}) {
  const editModal = useDisclosure();
  const deleteModal = useDisclosure();

  const [del] = useDeleteClientMutation({
    onCompleted() {
      onAfterDelete?.();
    },
    ...onError(),
  });

  const [update, updateReq] = useUpdateClientMutation({
    onCompleted(data) {
      const u = data.updateClient;
      runInAction(() => {
        client.name = u.name;
        client.image = u.image || undefined;
        client.workspaceCompany = u.workspaceCompany;
      });
      onAfterUpdate?.(data.updateClient);
      editModal.onClose();
    },
    ...onError(),
  });

  return (
    <React.Fragment>
      <Menu>
        {trigger}
        <MenuList>
          {can("client_update") && (
            <MenuItem {...editModal.getButtonProps()}>{t`Edit`}</MenuItem>
          )}
          {can("client_delete") && (
            <MenuItem {...deleteModal.getButtonProps()}>{t`Delete`}</MenuItem>
          )}
        </MenuList>
      </Menu>

      {client && (
        <ModalClient
          {...editModal.getDisclosureProps()}
          {...editModal}
          client={client}
          title={<Trans>Edit client</Trans>}
          isLoading={updateReq.loading}
          onSubmit={async (vars) => {
            await update({ variables: { input: vars } });
          }}
        />
      )}

      <ModalConfirm
        {...deleteModal.getDisclosureProps()}
        {...deleteModal}
        onConfirm={() => del({ variables: { id: client?.id } })}
      />
    </React.Fragment>
  );
});
